import { API_BASE_URL } from "../../../config/apiConfig";
// DiscoverService.js - SIMPLIFIED VERSION

import axios from 'axios';
import { useContext } from 'react';
import { AuthContext } from '../../../Context/AuthContext';

/* ======================================================
   IMPORT COUNTRY DATA FROM SEPARATE FILE (Optional)
====================================================== */
import {
  getCountryName as getCountryNameFromCode,
  getCountryCodeForCity,
  cityToCountryMap
} from '../../../utils/cityCountryUtils';

import {
  getCountryName,
  getCountryCode as getCountryCodeFromName
} from './countryCodes';

/* ======================================================
   AXIOS BACKEND CLIENT
====================================================== */
const createApiClient = (token = null) => {
  const client = axios.create({
    baseURL: `${API_BASE_URL}`,
    timeout: 30000,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  });

  client.interceptors.request.use((config) => {
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      console.error('API Error:', error.response?.status, error.response?.data);
      return Promise.reject(error);
    }
  );

  return client;
};

/* ======================================================
   HELPERS
====================================================== */
const normalizeCityName = (cityName) => {
  if (!cityName) return '';
  return String(cityName).trim().toLowerCase();
};

/* ======================================================
   HARDCODED CITY TO COUNTRY MAPPING
====================================================== */
const cityCountryMap = {
  // Egyptian Cities
  'cairo': 'EG',
  'alexandria': 'EG',
  'hurghada': 'EG',
  'sharm el sheikh': 'EG',
  'luxor': 'EG',
  'aswan': 'EG',
  'port said': 'EG',
  'suez': 'EG',
  'marsa alam': 'EG',
  'taba': 'EG',
  'assiut': 'EG',
  'sohag': 'EG',
  'el arish': 'EG',
  'borg el arab': 'EG',
  'st catherine': 'EG',
  'dahab': 'EG',
  'minya': 'EG',
  'beni suef': 'EG',
  'qena': 'EG',
  'siwa': 'EG',
  'bahariya': 'EG',
  'faiyum': 'EG',
  
  // International Cities
  'paris': 'FR',
  'london': 'GB',
  'new york': 'US',
  'los angeles': 'US',
  'chicago': 'US',
  'toronto': 'CA',
  'vancouver': 'CA',
  'montreal': 'CA',
  'sydney': 'AU',
  'melbourne': 'AU',
  'brisbane': 'AU',
  'perth': 'AU',
  'tokyo': 'JP',
  'osaka': 'JP',
  'kyoto': 'JP',
  'dubai': 'AE',
  'abu dhabi': 'AE',
  'sharjah': 'AE',
  'mumbai': 'IN',
  'delhi': 'IN',
  'bangalore': 'IN',
  'chennai': 'IN',
  'shanghai': 'CN',
  'beijing': 'CN',
  'guangzhou': 'CN',
  'shenzhen': 'CN',
  'hong kong': 'HK',
  'moscow': 'RU',
  'saint petersburg': 'RU',
  'berlin': 'DE',
  'frankfurt': 'DE',
  'munich': 'DE',
  'hamburg': 'DE',
  'rome': 'IT',
  'milan': 'IT',
  'venice': 'IT',
  'madrid': 'ES',
  'barcelona': 'ES',
  'valencia': 'ES',
  'amsterdam': 'NL',
  'rotterdam': 'NL',
  'brussels': 'BE',
  'antwerp': 'BE',
  'vienna': 'AT',
  'salzburg': 'AT',
  'zurich': 'CH',
  'geneva': 'CH',
  'bangkok': 'TH',
  'phuket': 'TH',
  'chiang mai': 'TH',
  'singapore': 'SG',
  'seoul': 'KR',
  'busan': 'KR',
  'istanbul': 'TR',
  'ankara': 'TR',
  'antalia': 'TR',
  'lisbon': 'PT',
  'porto': 'PT',
  'dublin': 'IE',
  'cork': 'IE',
  'prague': 'CZ',
  'brno': 'CZ',
  'warsaw': 'PL',
  'krakow': 'PL',
  'budapest': 'HU',
  'athens': 'GR',
  'thessaloniki': 'GR',
  'helsinki': 'FI',
  'tampere': 'FI',
  'oslo': 'NO',
  'bergen': 'NO',
  'stockholm': 'SE',
  'gothenburg': 'SE',
  'copenhagen': 'DK',
  'aarhus': 'DK',
  'reykjavik': 'IS',
  'doha': 'QA',
  'manama': 'BH',
  'kuwait city': 'KW',
  'riyadh': 'SA',
  'jeddah': 'SA',
  'dammam': 'SA',
  'muscat': 'OM',
  'amman': 'JO',
  'beirut': 'LB',
  'casablanca': 'MA',
  'marrakech': 'MA',
  'tunis': 'TN',
  'algiers': 'DZ',
  'nairobi': 'KE',
  'cape town': 'ZA',
  'johannesburg': 'ZA',
  'durban': 'ZA',
  'accra': 'GH',
  'lagos': 'NG',
  'abuja': 'NG',
  'dakar': 'SN',
  'addis ababa': 'ET',
  'dar es salaam': 'TZ',
  'kampala': 'UG',
  'kigali': 'RW',
  'windhoek': 'NA',
  'victoria falls': 'ZW',
  'cancun': 'MX',
  'mexico city': 'MX',
  'guadalajara': 'MX',
  'sao paulo': 'BR',
  'rio de janeiro': 'BR',
  'brasilia': 'BR',
  'buenos aires': 'AR',
  'santiago': 'CL',
  'lima': 'PE',
  'bogota': 'CO',
  'caracas': 'VE',
  'quito': 'EC',
  'havana': 'CU',
  'kingston': 'JM',
  'san juan': 'PR',
  'nassau': 'BS',
  'phnom penh': 'KH',
  'ho chi minh city': 'VN',
  'hanoi': 'VN',
  'vientiane': 'LA',
  'yangon': 'MM',
  'ulaanbaatar': 'MN',
  'tashkent': 'UZ',
  'astana': 'KZ',
  'baku': 'AZ',
  'yerevan': 'AM',
  'tbilisi': 'GE',
  'tehran': 'IR',
  'islamabad': 'PK',
  'karachi': 'PK',
  'lahore': 'PK',
  'dhaka': 'BD',
  'colombo': 'LK',
  'kathmandu': 'NP',
  'thimphu': 'BT',
  'male': 'MV',
  'manila': 'PH',
  'cebu': 'PH',
  'jakarta': 'ID',
  'bali': 'ID',
  'surabaya': 'ID',
  'kuala lumpur': 'MY',
  'penang': 'MY',
  
  // Additional European Cities
  'bratislava': 'SK',
  'ljubljana': 'SI',
  'zagreb': 'HR',
  'belgrade': 'RS',
  'sarajevo': 'BA',
  'tirana': 'AL',
  'skopje': 'MK',
  'sofia': 'BG',
  'bucharest': 'RO',
  'chisinau': 'MD',
  'kiev': 'UA',
  'odessa': 'UA',
  'riga': 'LV',
  'vilnius': 'LT',
  'tallinn': 'EE',
  'nicosia': 'CY',
  'valletta': 'MT',
  
  // Additional Middle Eastern Cities
  'tel aviv': 'IL',
  'jerusalem': 'IL',
  'haifa': 'IL',
  'medina': 'SA',
  'mecca': 'SA',
  'al ain': 'AE',
  'fujairah': 'AE',
  'ras al khaimah': 'AE',
  'salalah': 'OM',
  'sanaa': 'YE',
  'aden': 'YE',
  'baghdad': 'IQ',
  'basra': 'IQ',
  'erbil': 'IQ',
  'damascus': 'SY',
  'aleppo': 'SY',
  'tripoli': 'LY',
  'benghazi': 'LY'
};

/* ======================================================
   SIMPLE COUNTRY LOOKUP
====================================================== */
class CountryLookupService {
  static getCountryCodeFromCity(cityName) {
    const normalized = normalizeCityName(cityName);
    if (!normalized) return null;
    
    const cityLower = normalized.toLowerCase();
    
    // 1. Check hardcoded city-country map
    if (cityCountryMap[cityLower]) {
      const countryCode = cityCountryMap[cityLower];
      return {
        countryCode: countryCode,
        countryName: getCountryName(countryCode) || getCountryNameFromCode(countryCode) || countryCode,
        cityName: cityName,
        source: 'hardcoded-map'
      };
    }
    
    // 2. If you still want to use the imported function as fallback (optional)
    try {
      const countryCode = getCountryCodeForCity(cityLower);
      if (countryCode) {
        return {
          countryCode: countryCode,
          countryName: getCountryName(countryCode) || getCountryNameFromCode(countryCode) || countryCode,
          cityName: cityName,
          source: 'city-country-utils'
        };
      }
    } catch (error) {
      //console.log('cityCountryUtils not available:', error.message);
    }
    
    // 3. Return null if no match found
    return null;
  }
  
  static getCountryName(countryCode) {
    if (!countryCode) return '';
    const code = countryCode.length > 2 ? countryCode.substring(0, 2).toUpperCase() : countryCode.toUpperCase();
    try {
      return getCountryName(code) || getCountryNameFromCode(code) || code;
    } catch {
      return code;
    }
  }
  
  static getCountryCode(countryName) {
    try {
      return getCountryCodeFromName(countryName) || '';
    } catch {
      return '';
    }
  }
}

/* ======================================================
   HOOK VERSION
====================================================== */
export const useDiscoverService = () => {
  const { token } = useContext(AuthContext);
  
  if (!token) {
    console.warn('No auth token found in useDiscoverService');
  }

  const client = createApiClient(token);
  return createDiscoverService(client);
};

/* ======================================================
   CORE SERVICE FACTORY
====================================================== */
const createDiscoverService = (client) => ({
  /* ======================================================
     GET AIRPORTS - MAIN ENTRY POINT
  ====================================================== */
  async getAirports(params, countryCodeParam = null) {
    let cityName
    try {
      let cityName, countryCode;

      if (typeof params === 'object') {
        cityName = params.cityName || params;
        countryCode = params.countryCode || null;
      } else {
        cityName = params;
        countryCode = countryCodeParam || null;
      }

      const normalizedCityName = normalizeCityName(cityName);
      if (!normalizedCityName || normalizedCityName.length < 2) {
        throw new Error('City name is required and must be at least 2 characters');
      }

      const cityLower = normalizedCityName.toLowerCase();
      
      // Clean country code
      let finalCountryCode = countryCode;
      if (finalCountryCode && finalCountryCode.length > 2) {
        finalCountryCode = finalCountryCode.substring(0, 2).toUpperCase();
      }

      // If no country code provided, try to find it from hardcoded map
      if (!finalCountryCode) {
        const countryInfo = CountryLookupService.getCountryCodeFromCity(normalizedCityName);
        if (countryInfo) {
          finalCountryCode = countryInfo.countryCode;
        }
      }

      // Call backend API with city and country code
      // console.log('Calling backend API with:', {
      //   cityName: normalizedCityName,
      //   countryCode: finalCountryCode
      // });
      
      const response = await client.post('/discover/airports', {
        cityName: normalizedCityName,
        countryCode: finalCountryCode
      });

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const normalizedAirports = response.data
          .map((airport, index) => ({
            id: airport.id || index,
            code: airport.code || airport.iata || airport.icao || '',
            name: airport.name?.trim() || `Airport ${index + 1}`,
            city: airport.city?.trim() || normalizedCityName,
            country: airport.country?.trim() || CountryLookupService.getCountryName(finalCountryCode),
            countryCode: airport.countryCode || finalCountryCode || ''
          }))
          .filter(airport => airport.code && airport.name)
          .slice(0, 10);

        return normalizedAirports;
      } else {
        console.warn('Backend returned no airports for:', normalizedCityName);
        return [];
      }

    } catch (error) {
      console.error('Error in getAirports:', error);
      
      // Minimal fallback for critical cities only
      const fallbackAirports = {
        'cairo': [{
          id: 1,
          code: 'CAI',
          name: 'Cairo International Airport',
          city: 'Cairo',
          country: 'Egypt',
          countryCode: 'EG'
        }],
        'paris': [{
          id: 2,
          code: 'CDG',
          name: 'Charles de Gaulle Airport',
          city: 'Paris',
          country: 'France',
          countryCode: 'FR'
        }],
        'london': [{
          id: 3,
          code: 'LHR',
          name: 'Heathrow Airport',
          city: 'London',
          country: 'United Kingdom',
          countryCode: 'GB'
        }]
      };
      
      const cityLower = cityName?.toLowerCase();
      if (cityLower && fallbackAirports[cityLower]) {
        return fallbackAirports[cityLower];
      }
      
      // Return empty array instead of throwing error
      return [];
    }
  },

  /* ======================================================
     GET POSSIBLE COUNTRIES FOR CITY
  ====================================================== */
  async getPossibleCountriesForCity(cityName) {
    try {
      const normalizedCityName = normalizeCityName(cityName);
      if (!normalizedCityName) return [];

      const results = [];
      const cityLower = normalizedCityName.toLowerCase();

      // Check hardcoded city-country map
      if (cityCountryMap[cityLower]) {
        const countryCode = cityCountryMap[cityLower];
        results.push({
          countryCode: countryCode,
          countryName: CountryLookupService.getCountryName(countryCode),
          cityName: normalizedCityName,
          source: 'hardcoded-map'
        });
      }

      // Optional: Try imported function if available
      try {
        const countryCode = getCountryCodeForCity(cityLower);
        if (countryCode && !results.find(r => r.countryCode === countryCode)) {
          results.push({
            countryCode: countryCode,
            countryName: CountryLookupService.getCountryName(countryCode),
            cityName: normalizedCityName,
            source: 'city-country-utils'
          });
        }
      } catch (error) {
        //console.log('cityCountryUtils not available:', error.message);
      }

      return results;

    } catch (error) {
      console.error('Error in getPossibleCountriesForCity:', error);
      return [];
    }
  },

  /* ======================================================
     GET COUNTRY NAME FROM CODE
  ====================================================== */
  getCountryName(countryCode) {
    return CountryLookupService.getCountryName(countryCode);
  },

  /* ======================================================
     GET COUNTRY CODE FROM NAME
  ====================================================== */
  getCountryCode(countryName) {
    return CountryLookupService.getCountryCode(countryName);
  },

  /* ======================================================
     GET ALL COUNTRIES
  ====================================================== */
  getAllCountries() {
    try {
      // Use the imported city-country map if available
      const allCountryCodes = Object.keys(cityToCountryMap || {});
      return allCountryCodes.map(code => ({
        code: code,
        name: getCountryNameFromCode(code) || code
      }));
    } catch (error) {
      //console.log('cityToCountryMap not available:', error.message);
      return [];
    }
  },

  /* ======================================================
     SEARCH FLIGHTS
  ====================================================== */
  async searchFlights(params) {
    try {
      const response = await client.post('/discover/flights', params);
      return response.data;
    } catch (error) {
      console.error('Error in searchFlights:', error);
      throw error;
    }
  },

  /* ======================================================
     SEARCH HOTELS
  ====================================================== */
  async searchHotels(params) {
    try {
      const response = await client.post('/discover/hotels', params);
      return response.data;
    } catch (error) {
      console.error('Error in searchHotels:', error);
      throw error;
    }
  },

  /* ======================================================
     SEARCH ACTIVITIES
  ====================================================== */
  async searchActivities(params) {
    try {
      const response = await client.post('/discover/activities', params);
      return response.data;
    } catch (error) {
      console.error('Error in searchActivities:', error);
      throw error;
    }
  },

  /* ======================================================
     BOOK FLIGHT
  ====================================================== */
  async bookFlight(bookingData) {
    try {
      const response = await client.post('/discover/book-flight', bookingData);
      return response.data;
    } catch (error) {
      console.error('Error in bookFlight:', error);
      throw error;
    }
  }
});

/* ======================================================
   DEFAULT EXPORT
====================================================== */
const getTokenFromStorage = () => {
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
};

const DiscoverService = createDiscoverService(
  createApiClient(getTokenFromStorage())
);

export default DiscoverService;